import { parseFinanceRoute } from '#pplx/frontend/apps/ask/finance/routes.js';
import { useCollectionQuery } from '#pplx/frontend/apps/ask/hooks/collections-hooks/useCollectionQuery.js';
import { usePasteShortcutQuery } from '#pplx/frontend/apps/ask/hooks/tasks-hooks/usePasteShortcutQuery.js';
import { useArticleResults } from '#pplx/frontend/apps/ask/providers/ArticleResultsProviderShared.js';
import { useResults } from '#pplx/frontend/apps/ask/providers/ResultsContext.js';
import { ONBOARDING_SLUG } from '#pplx/frontend/apps/ask/routing/onboarding.js';
import { FinanceRoutesRegexSpa, SearchRoutesRegex, } from '#pplx/frontend/apps/ask/routing/routeRegexes.js';
import { getComputerPageDescription, getComputerStaticTitle, getComputerTaskDescription, } from '#pplx/frontend/apps/ask/spa/metadata/computerMetadataTitles.js';
import { COMPUTER_FAVICON_HREF, COMPUTER_PAGE_TITLE, LIBRARY_PAGE_TITLE, PROJECTS_PAGE_TITLE, SPACES_PAGE_TITLE, generateAcademicMetadataSync, generateArticlePageMetadataSync, generateComputerPageMetadataSync, generateDiscoverMetadataSync, generateFinanceMetadataSync, generatePageMetadataSync, generatePatentsMetadataSync, generatePerplexifyMeMetadataSync, generateShortcutSettingsMetadataSync, generateSpaceMetadataSync, generateThreadPageMetadataSync, } from '#pplx/frontend/apps/ask/spa/metadata/metadata.js';
import { useFinanceRouteData } from '#pplx/frontend/apps/ask/spa/metadata/routeData.js';
import { useComputerAssetDocumentTitle } from '#pplx/frontend/apps/ask/spa/metadata/useComputerAssetDocumentTitle.js';
import { useComputerSkillDocumentTitle } from '#pplx/frontend/apps/ask/spa/metadata/useComputerSkillDocumentTitle.js';
import { useComputerThreadDocumentTitle } from '#pplx/frontend/apps/ask/spa/metadata/useComputerThreadDocumentTitle.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { matchRoute } from '#pplx/frontend/router/UrlUtils.js';
import { usePathname } from '#pplx/frontend/router/usePathname.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useIntl } from 'react-intl';
export const useMetadata = (spaRoutes) => {
    const reason = 'home-metadata';
    const { $t } = useIntl();
    const { device: { userAgent }, } = useWebPlatform();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const matchedRoute = matchRoute(spaRoutes, pathname ?? '');
    const { route: _route, params } = matchedRoute ?? {};
    const route = _route;
    const computerAssetDocumentTitle = useComputerAssetDocumentTitle({
        slug: params?.slug,
        view: searchParams?.get('view'),
        enabled: route === '/computer/a/:slug',
    });
    const computerSkillDocumentTitle = useComputerSkillDocumentTitle({
        slug: params?.slug,
        skillId: searchParams?.get('skillId'),
        enabled: route === '/computer/skills/:slug',
    });
    const { firstResult } = useResults();
    const computerThreadTitle = useComputerThreadDocumentTitle(route === '/computer/t/:slug' || route === '/session/:slug' || route === '/computer/tasks/:slug'
        ? params?.slug
        : undefined);
    const isSpacesRoute = route === '/spaces/:slug/:threadSlug' ||
        route === '/projects/:slug/:threadSlug' ||
        route === '/spaces/:slug' ||
        route === '/projects/:slug';
    const { collection } = useCollectionQuery({
        collectionSlug: isSpacesRoute ? params?.slug : undefined,
        reason,
        enabled: isSpacesRoute,
    });
    const { article } = useArticleResults();
    const routeParams = parseFinanceRoute(pathname);
    const financeRouteData = useFinanceRouteData(routeParams);
    const { query: { data: shortcutData }, } = usePasteShortcutQuery({
        reason,
    });
    if (!pathname || !route || !params) {
        return generatePageMetadataSync({
            index: true,
            userAgent,
        });
    }
    switch (route) {
        case '/spaces/:slug/:threadSlug':
        case '/projects/:slug/:threadSlug':
        case '/spaces/:slug':
        case '/projects/:slug':
            return generateSpaceMetadataSync({
                canonicalPathPrefix: 'projects',
                slug: params.slug,
                userAgent,
                collection,
            });
        case '/page/:slug':
            return generateArticlePageMetadataSync({
                slug: params.slug,
                userAgent,
                articleSection: article?.[0],
                audience: 'in-app',
            });
        case '/academic':
            return generateAcademicMetadataSync({
                userAgent,
            });
        case '/discover':
        case '/discover/:topic':
            return generateDiscoverMetadataSync({
                userAgent,
            });
        case '/patents':
            return generatePatentsMetadataSync({
                userAgent,
            });
        case '/perplexify_me':
            return generatePerplexifyMeMetadataSync({
                userAgent,
            });
        case FinanceRoutesRegexSpa:
            return generateFinanceMetadataSync({
                userAgent,
                pathname,
                financeRouteData,
                $t,
            });
        case '/cfo/:tab?':
            return generatePageMetadataSync({
                index: true,
                title: 'Personal CFO',
                userAgent,
            });
        case SearchRoutesRegex:
            // `in-app`: this feeds `DocumentTitle` only. The viewer is already
            // looking at the thread, so the access-level gate that applies to
            // crawlers would just blank the owner's browser tab.
            return generateThreadPageMetadataSync({
                slug: params.uuid,
                userAgent,
                firstEntity: firstResult,
                audience: 'in-app',
            });
        case '/discover/:topic/:slug':
            return generateArticlePageMetadataSync({
                slug: params.slug,
                userAgent,
                articleSection: article?.[0],
                audience: 'in-app',
            });
        case '/b/home':
            return generatePageMetadataSync({
                title: 'New Tab',
                index: true,
                userAgent,
            });
        case '/chrome/new-tab':
            return generatePageMetadataSync({
                title: 'New Tab',
                index: false,
                userAgent,
            });
        case '/account':
        case '/account/details':
        case '/account/org/details':
        case '/account/org/identity':
        case '/account/org/data-privacy':
        case '/account/org/encryption':
        case '/account/org/permissions':
        case '/account/org/roles-permissions':
        case '/account/org/roles-permissions/:roleId':
        case '/account/org/roles-permissions/groups/:groupId':
        case '/account/org/apps':
        case '/account/org/connectors':
        case '/account/org/credentials-vault':
        case '/account/org/skills':
        case '/account/org/plugins':
        case '/account/org/plugins/skills/:skillId':
        case '/account/org/plugins/:pluginId':
        case '/account/org/enterprise-apis':
        case '/account/org/analytics':
        case '/account/org/computer':
        case '/account/org/comet':
        case '/account/org/comet-telemetry':
        case '/account/org/credits-usage':
        case '/account/org/billing':
        case '/account/org/credits-usage/:creditLimitId':
        case '/account/org':
        case '/account/api/group':
        case '/account/api/members':
        case '/account/api/billing':
        case '/account/api/keys':
        case '/account/api/playground/search':
        case '/account/api/limits':
        case '/account/api/files':
        case '/account/api/files/:repositoryUuid':
        case '/account/api':
        case '/account/pro-perks':
        case '/account/pro-perks/:name':
        case '/account/tasks/:taskId':
        case '/account/tasks':
        case '/account/org/members':
        case '/account/preferences':
        case '/account/personalize/memories':
        case '/account/personalize':
        case '/account/memory':
        case '/account/computer':
        case '/account/integrations':
        case '/account/devices':
        case '/account/site-instructions':
        case '/account/notifications':
        case '/account/analytics':
        case '/account/usage':
        case '/account/credits':
        case '/account/connectors':
        case '/account/credentials-vault':
            return generatePageMetadataSync({
                title: 'Perplexity Account',
                index: true,
                userAgent,
            });
        case '/account/shortcuts':
            return generateShortcutSettingsMetadataSync({
                shortcut: shortcutData?.shortcut ?? undefined,
            });
        case '/computer/t/:slug':
        case '/session/:slug':
        case '/computer/tasks/:slug': {
            if (params.slug === ONBOARDING_SLUG) {
                return generatePageMetadataSync({ index: false, userAgent });
            }
            // Deliberately no `query_str` fallback: the raw user query must not reach
            // `document.title`, where any third-party tag on the page can read it.
            return generatePageMetadataSync({
                index: false,
                title: computerThreadTitle,
                description: getComputerTaskDescription($t),
                imageURL: `https://www.perplexity.ai/api/og/computer/${params.slug}`,
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        }
        case '/computer/tasks':
        case '/computer/automations':
        case '/computer/automations/:automationId':
        case '/computer/automations/:automationId/runs/:runId':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle(route === '/computer/tasks' ? route : '/computer/automations', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/gallery/:slug':
        case '/computer/gallery':
        case '/computer/live/:slug?': {
            const liveTitle = firstResult?.thread_title || COMPUTER_PAGE_TITLE;
            if (!params.slug) {
                return generatePageMetadataSync({
                    index: true,
                    title: liveTitle,
                    description: getComputerTaskDescription($t),
                    imageURL: 'https://r2cdn.perplexity.ai/computer/og.webp',
                    faviconHref: COMPUTER_FAVICON_HREF,
                    userAgent,
                });
            }
            return generatePageMetadataSync({
                index: true,
                title: liveTitle,
                description: getComputerTaskDescription($t),
                imageURL: `https://www.perplexity.ai/api/og/computer/${params.slug}`,
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        }
        case '/computer/new':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle('/computer/new', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/artifacts':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle('/computer/artifacts', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/skills/:slug':
            return generateComputerPageMetadataSync({
                title: computerSkillDocumentTitle,
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/skills':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle('/computer/skills', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/plugins/categories/:categoryKey':
        case '/computer/plugins/skills/:skillId':
        case '/computer/plugins/connectors/:connectorId':
        case '/computer/plugins/:pluginId':
        case '/computer/plugins':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle('/computer/plugins', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/workflows':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle('/computer/workflows', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/memory':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle('/computer/memory', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/connectors':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle('/computer/connectors', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/credentials-vault':
            return generateComputerPageMetadataSync({
                title: getComputerStaticTitle('/computer/credentials-vault', $t),
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/landing/:slug':
        case '/computer/landing':
        case '/computer':
            return generateComputerPageMetadataSync({
                title: COMPUTER_PAGE_TITLE,
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/computer/a/:slug':
            return generateComputerPageMetadataSync({
                title: computerAssetDocumentTitle,
                description: getComputerPageDescription($t),
                faviconHref: COMPUTER_FAVICON_HREF,
                userAgent,
            });
        case '/library':
            return generatePageMetadataSync({
                index: true,
                title: LIBRARY_PAGE_TITLE,
                userAgent,
            });
        case '/':
        case '/__blank__':
        case '/__internal__/wizard':
        case '/__internal__/workflow/teammate':
        case '/__internal__/workflow/agents':
        case '/__internal__/workflow/assets':
        case '/__internal__/workflow/widgets':
        case '/__internal__/workflow/icons':
        case '/__internal__/workflow':
        case '/b/zen':
        case '/spaces':
            return generatePageMetadataSync({
                index: true,
                title: SPACES_PAGE_TITLE,
                userAgent,
            });
        case '/projects':
            return generatePageMetadataSync({
                index: true,
                title: PROJECTS_PAGE_TITLE,
                userAgent,
            });
        case '/composer-sdk':
        case '/health':
        case '/account/health':
        case undefined:
            return generatePageMetadataSync({
                index: true,
                userAgent,
            });
        case '/teammate/:uuid':
        case '/security':
        case '/security/sources/:source_id':
        case '/b/cloud-picker':
        case '/onboarding-neo':
            return generatePageMetadataSync({
                index: false,
                userAgent,
            });
        default:
            assertUnreachableValue(route);
    }
};
//# sourceMappingURL=useMetadata.js.map